import { observer } from 'mobx-react-lite'
import React, { useRef, ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Radom, { PayoutAmountMode, PayoutFeeEstimate, WithdrawalAccount } from '../state/Radom'
import Treasury from '../state/Treasury'
import User from '../state/User'
import FiatRamps from '../state/FiatRamps'
import { getMethod, methodsForTicker, methodNetworkName, methodTokenAddress } from '../util/Managed'
import { assetName, assetSubtitle, methodNetworkLabel, networkName, tickerLabel } from '../util/AssetLabels'
import { AssetIcon, AssetRow, NetworkIcon, NetworkRow } from './AssetIdentity'
import { errorToast, formatAssetAmount, shortAddress, successToast, useTestMode } from '../util/Util'
import { PrimaryButton, SecondaryButton } from './Button'
import Dropdown, { DropdownContext, DropdownItem, DropdownSearchContainer, DropdownTextInput } from './Dropdown'
import { InputLabel } from './Input'
import LoadingBar from './LoadingBar'
import Modal, { ModalFooter } from './Modal'
import NumberInput from './NumberInput'
import BRLLogo from '../images/BRLLogo.svg'
import EURLogo from '../images/EUROLogo.svg'
import GBPLogo from '../images/GBPLogo.svg'
import MXNLogo from '../images/MXNLogo.svg'
import USDLogo from '../images/USDLogo.svg'
import Gate from './Gate'

const ETHEREUM_USDC_TOKEN = '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'
const ETHEREUM_USDT_TOKEN = '0xdac17f958d2ee523a2206206994597c13d831ec7'
const SEPOLIA_USDC_TOKEN = '0xa4fce8264370437e718ae207805b4e6233638b9e'

const WithdrawalRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-grow: 1;
  cursor: default;
`

const SectionCard = styled.div<{ $embedded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $embedded }) => $embedded ? '14px' : '10px'};
  padding: ${({ $embedded }) => $embedded ? '0' : '10px'};
  border-radius: ${({ $embedded }) => $embedded ? '0' : '13px'};
  border: ${({ $embedded }) => $embedded ? '0' : '1px solid var(--border-soft, #eceff3)'};
  background: ${({ $embedded }) => $embedded ? 'transparent' : 'var(--surface-subtle, #fbfcfe)'};
`

const AssetSummaryCard = styled.div`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e7ebf2);
  border-radius: 13px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 11px 13px;
`

const EstimateRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 14px;
  min-height: 22px;
`

const EstimateLabel = styled.span`
  color: var(--text-muted, #6b7280);
  font-size: 12.5px;
`

const EstimateValue = styled.span`
  color: var(--ink, #20242d);
  font-size: 12.75px;
  font-weight: 500;
  text-align: right;
`

export interface WithdrawableAsset {
  network: string
  token?: string
  asset?: string
  maxAmount: string
  treasuryId?: string
}

interface IProps {
  modalOpen: boolean
  onModalOpen: (open: boolean) => void
  assetToWithdraw?: WithdrawableAsset
  withdrawableAssets?: WithdrawableAsset[]
  embedded?: boolean
  initialWithdrawalAccountId?: string
  initialWithdrawalKind?: WithdrawalKind
  hideWithdrawalKindSelector?: boolean
  onWithdrawalKindChange?: (kind: WithdrawalKind) => void
  onCreateWithdrawalAccount?: (kind: WithdrawalKind) => void
}

type WithdrawalKind = 'cash' | 'crypto'
type CashWithdrawalCurrency = 'EUR' | 'USD' | 'GBP' | 'BRL' | 'MXN'

const cashCurrencyConfig: Array<{
  currency: CashWithdrawalCurrency
  label: string
  logo: string
  enabled: boolean
  unavailableLabel?: string
}> = [
  { currency: 'EUR', label: assetName('EUR'), logo: EURLogo, enabled: true },
  { currency: 'USD', label: assetName('USD'), logo: USDLogo, enabled: true },
  { currency: 'GBP', label: assetName('GBP'), logo: GBPLogo, enabled: false },
  { currency: 'BRL', label: assetName('BRL'), logo: BRLLogo, enabled: false },
  { currency: 'MXN', label: assetName('MXN'), logo: MXNLogo, enabled: false }
]

// Saved fiat withdrawal accounts don't store an explicit currency, so derive it
// from the metadata they do have: US bank details are USD, and IBANs are EUR
// (SEPA) unless the IBAN country code indicates a non-euro currency.
const NON_EURO_IBAN_CURRENCIES: Record<string, string> = {
  GB: 'GBP',
  CH: 'CHF',
  DK: 'DKK',
  NO: 'NOK',
  SE: 'SEK',
  IS: 'ISK',
  PL: 'PLN',
  CZ: 'CZK',
  HU: 'HUF',
  RO: 'RON',
  BG: 'BGN'
}

const getWithdrawalAccountCurrency = (account?: WithdrawalAccount): string | undefined => {
  const ibanDetails = account?.accountDetails.Fiat?.accountType.iban
  if (ibanDetails) {
    // The iban field arrives masked, so the country comes from the API's
    // ibanCountry; the prefix is only a fallback for unmasked values.
    const prefix = ibanDetails.iban?.replace(/\s+/g, '').slice(0, 2).toUpperCase()
    const countryCode = ibanDetails.ibanCountry ?? (/^[A-Z]{2}$/.test(prefix ?? '') ? prefix : undefined)
    if (!countryCode) {
      return undefined
    }
    return NON_EURO_IBAN_CURRENCIES[countryCode] ?? 'EUR'
  }

  if (account?.accountDetails.Fiat?.accountType.us) {
    return 'USD'
  }
}

// A country-based currency guess can be wrong (GB-held EUR accounts exist), so
// mismatches are surfaced and sorted last rather than hidden.
const hasLikelyCurrencyMismatch = (account: WithdrawalAccount, cashCurrency: string): boolean => {
  if (!account.accountDetails.Fiat?.accountType.iban) {
    return false
  }
  const accountCurrency = getWithdrawalAccountCurrency(account)
  return !!accountCurrency && accountCurrency !== cashCurrency
}

const AssetWithdrawalModal = observer((props: IProps): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()

  // One key per logical submission: stable across retries after an error (so a
  // timed-out request replays instead of double-paying), fresh after success.
  const cashSubmissionKey = useRef(crypto.randomUUID())
  const [amountToWithdraw, setWithdrawalAmount] = useState('')
  const [amountMode, setAmountMode] = useState<PayoutAmountMode>('recipient')
  const [withdrawalKind, setWithdrawalKind] = useState<WithdrawalKind>(
    FiatRamps.cashWithdrawalEnabled ? (props.initialWithdrawalKind ?? 'cash') : 'crypto'
  )
  const [cashCurrency, setCashCurrency] = useState<CashWithdrawalCurrency>('EUR')
  const [selectedAsset, setSelectedAsset] = useState<WithdrawableAsset>(
    props.assetToWithdraw ?? props.withdrawableAssets?.[0] ?? { maxAmount: '', network: 'Ethereum' }
  )
  const [withdrawalAccount, setWithdrawalAccount] = useState<WithdrawalAccount>()
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const senderPaysFees = false

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])
  const [assetSearch, setAssetSearch] = useState('')
  const [accountSearch, setAccountSearch] = useState('')
  const [cashCurrencySearch, setCashCurrencySearch] = useState('')
  const [feeEstimate, setFeeEstimate] = useState<PayoutFeeEstimate | null>(null)
  const [isEstimatingFee, setIsEstimatingFee] = useState(false)
  const isSameAsset = (a: WithdrawableAsset, b: WithdrawableAsset): boolean =>
    (a.asset && b.asset ? a.asset === b.asset : a.network === b.network && a.token === b.token) &&
    a.treasuryId === b.treasuryId
  const selectedTreasuryId = selectedAsset.treasuryId
  const destMethods = selectedAsset.asset
    ? methodsForTicker(selectedAsset.asset, testMode)
    : []
  const selectedMethod = useMemo(
    () => getMethod(selectedAsset.network, selectedAsset.token),
    [selectedAsset]
  )
  const selectedFiatAccount = withdrawalAccount?.accountDetails.Fiat
  const selectedOwnerType = selectedFiatAccount?.accountOwnerType?.toLowerCase()
  const isSelectedIban = !!selectedFiatAccount?.accountType.iban
  const isSelectedUsAccount = !!selectedFiatAccount?.accountType.us
  const isSelectedBusinessIban = selectedOwnerType === 'business' && isSelectedIban
  const isSelectedIndividualIban = selectedOwnerType === 'individual' && isSelectedIban
  const usesLegacyEurOfframp = User.organization?.legacyEurOfframpEnabled === true
  const isIvyEurWithdrawal = withdrawalKind === 'cash' && cashCurrency === 'EUR' && !usesLegacyEurOfframp
  const expectedUsdcNetwork = testMode ? 'SepoliaTestnet' : 'Ethereum'
  const expectedUsdcToken = (testMode ? SEPOLIA_USDC_TOKEN : ETHEREUM_USDC_TOKEN).toLowerCase()
  const isUsdtFundingAsset = !testMode &&
    (selectedAsset.asset === 'USDT' ||
      (selectedAsset.network === 'Ethereum' &&
        (selectedMethod.ticker === 'USDT' || selectedAsset.token?.toLowerCase() === ETHEREUM_USDT_TOKEN)))
  const volatileCashFunding: Array<{ ticker: 'BTC' | 'ETH' | 'SOL', network: string }> = [
    { ticker: 'BTC', network: 'Bitcoin' },
    { ticker: 'ETH', network: 'Ethereum' },
    { ticker: 'SOL', network: 'Solana' }
  ]
  const selectedVolatileFunding = !testMode && (!selectedAsset.token || selectedAsset.asset)
    ? volatileCashFunding.find(v => v.ticker === selectedMethod.ticker &&
      (selectedAsset.asset === v.ticker || v.network === selectedAsset.network))
    : undefined
  const isSupportedIvyFundingAsset =
    selectedMethod.ticker === 'USDC' ||
    (selectedAsset.network === expectedUsdcNetwork &&
      (selectedMethod.ticker === 'USDC' || selectedAsset.token?.toLowerCase() === expectedUsdcToken)) ||
    isUsdtFundingAsset ||
    !!selectedVolatileFunding
  const selectedFundingCurrency: 'USDC' | 'USDT' | 'BTC' | 'ETH' | 'SOL' =
    selectedVolatileFunding?.ticker ?? (isUsdtFundingAsset ? 'USDT' : 'USDC')
  const isIvyBankWithdrawal = isIvyEurWithdrawal && isSelectedBusinessIban
  const availableAmount = selectedAsset.maxAmount
  const availableAmountLabel = `${formatAssetAmount(availableAmount, selectedMethod.ticker)} ${selectedMethod.ticker}`
  const estimatedTotalDebit = feeEstimate?.estimatedTotalDebitUsd
  const hasInsufficientBankFunding = isIvyEurWithdrawal &&
    !!estimatedTotalDebit &&
    Number(estimatedTotalDebit) > Number(availableAmount || 0)

  const filterWithdrawalAccount = (a: WithdrawalAccount): boolean => {
    if (selectedTreasuryId && !a.accountDetails.Crypto) {
      return false
    }

    if (withdrawalKind === 'crypto' && a.accountDetails.Crypto && a.accountDetails.Crypto.network === selectedAsset.network) {
      return true
    }

    if (withdrawalKind === 'cash' && a.accountDetails.Fiat) {
      // Rail-level fit only: USD cash pays US bank accounts, EUR cash pays
      // IBANs. Currency guesses from the IBAN country never hide an account
      // (GB-held EUR accounts are real) — mismatches are badged and sorted
      // last instead.
      if (cashCurrency === 'USD') {
        return !!a.accountDetails.Fiat.accountType.us
      }

      if (!a.accountDetails.Fiat.accountType.iban) {
        return false
      }

      const isBusinessIban = a.accountDetails.Fiat.accountOwnerType?.toLowerCase() === 'business'
      if (!usesLegacyEurOfframp && !isBusinessIban) {
        return false
      }

      return usesLegacyEurOfframp || isSupportedIvyFundingAsset
    }

    return false
  }

  const allAssets = props.withdrawableAssets?.length
    ? props.withdrawableAssets
    : [selectedAsset]
  const cashFundingAssets = allAssets.filter(asset => {
    if (asset.treasuryId) {
      return false
    }

    const method = getMethod(asset.network, asset.token)
    const token = asset.token?.toLowerCase()
    if (cashCurrency !== 'EUR' || usesLegacyEurOfframp) {
      return Number(asset.maxAmount) > 0
    }

    const isUsdc = asset.asset === 'USDC' || (asset.network === expectedUsdcNetwork &&
      (method.ticker === 'USDC' || token === expectedUsdcToken))
    const isUsdt = asset.asset === 'USDT' || (!testMode && asset.network === 'Ethereum' &&
      (method.ticker === 'USDT' || token === ETHEREUM_USDT_TOKEN))
    const isVolatile = !testMode && Number(asset.maxAmount) > 0 &&
      (asset.asset === 'BTC' || asset.asset === 'ETH' || asset.asset === 'SOL' ||
        (!asset.token && volatileCashFunding.some(v => v.network === asset.network && v.ticker === method.ticker)))
    return isUsdc || isUsdt || isVolatile
  })
  const selectableAssets = withdrawalKind === 'cash'
    ? cashFundingAssets
    : allAssets.filter(asset => Number(asset.maxAmount) > 0)
  // USD cash withdrawals run over the Bastion wire rail from Send. This modal
  // only renders when that rail is off for the organization, so USD is never
  // offered here; the legacy USD path behind it always fails.
  const cashCurrencyOptions = cashCurrencyConfig.map(option => option.currency === 'USD'
    ? {
        ...option,
        enabled: !!User.organization?.bankWithdrawals?.usdAssets.length,
        unavailableLabel: 'Not enabled for this organization'
      }
    : option)
  const searchedCashCurrencyOptions = cashCurrencyOptions.filter(option => {
    const query = cashCurrencySearch.trim().toLowerCase()
    if (!query) return true
    return [option.currency, option.label].some(term => term.toLowerCase().includes(query))
  })
  const searchedSelectableAssets = selectableAssets.filter(asset => {
    const query = assetSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const method = getMethod(asset.network, asset.token)
    const terms = [
      method.ticker,
      method.name,
      method.currencyName,
      method.hostChain?.name,
      asset.network,
      asset.token,
      asset.asset,
      asset.maxAmount
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })

  const filteredAccounts = withdrawalAccounts
    .filter(a => filterWithdrawalAccount(a))
    .sort((a, b) =>
      Number(hasLikelyCurrencyMismatch(a, cashCurrency)) - Number(hasLikelyCurrencyMismatch(b, cashCurrency)))
  const searchedAccounts = filteredAccounts.filter(account => {
    const query = accountSearch.trim().toLowerCase()
    if (!query) {
      return true
    }

    const fiat = account.accountDetails.Fiat
    const crypto = account.accountDetails.Crypto
    const terms = [
      account.name,
      account.id,
      crypto?.address,
      crypto?.network,
      fiat?.accountOwnerName,
      fiat?.accountOwnerType,
      fiat?.accountType.iban?.iban,
      fiat?.accountType.iban?.bic,
      fiat?.accountType.us?.accountNumber,
      fiat?.accountType.us?.routingNumber,
      fiat?.accountType.us?.bankName
    ]

    return terms.some(term => term?.toLowerCase().includes(query))
  })
  const hasIndividualIbanWithdrawalAccounts = withdrawalAccounts.some(a =>
    a.accountDetails.Fiat?.accountType.iban &&
    a.accountDetails.Fiat.accountOwnerType?.toLowerCase() === 'individual')
  const showIndividualIbanPayoutHint = isSupportedIvyFundingAsset &&
    withdrawalKind === 'cash' &&
    cashCurrency === 'EUR' &&
    filteredAccounts.length === 0 &&
    hasIndividualIbanWithdrawalAccounts

  useEffect(() => {
    if (withdrawalAccount && !filterWithdrawalAccount(withdrawalAccount)) {
      setWithdrawalAccount(undefined)
    }
  }, [selectedAsset, withdrawalAccount, withdrawalKind, cashCurrency])

  useEffect(() => {
    setWithdrawalAccount(undefined)
    setWithdrawalAmount('')
    setFeeEstimate(null)
  }, [cashCurrency])

  useEffect(() => {
    if (FiatRamps.cashWithdrawalEnabled && props.initialWithdrawalKind && props.initialWithdrawalKind !== withdrawalKind) {
      setWithdrawalKind(props.initialWithdrawalKind)
    }
  }, [props.initialWithdrawalKind])

  useEffect(() => {
    const nextAsset = withdrawalKind === 'cash'
      ? cashFundingAssets[0]
      : props.assetToWithdraw ?? allAssets[0]

    if (nextAsset && !selectableAssets.some(asset => isSameAsset(asset, selectedAsset))) {
      setSelectedAsset(nextAsset)
    }
  }, [withdrawalKind, cashCurrency, props.assetToWithdraw, props.withdrawableAssets?.length])

  useEffect(() => {
    const freshSelectedAsset = props.withdrawableAssets?.find(asset => isSameAsset(asset, selectedAsset))
    if (freshSelectedAsset && freshSelectedAsset.maxAmount !== selectedAsset.maxAmount) {
      setSelectedAsset(freshSelectedAsset)
    }
  }, [props.withdrawableAssets, selectedAsset])

  const renderAccountText = (account: WithdrawalAccount): React.ReactElement => {
    const accountName = account.name?.trim() || 'Withdrawal account'

    return <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
      {
        account.accountDetails.Crypto &&
        <NetworkIcon method={getMethod(account.accountDetails.Crypto.network)} size={20} />
      }
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          color: 'var(--ink, #20242d)',
          fontSize: 13.5,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {accountName}
        </span>
        {
          account.accountDetails.Crypto &&
          <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {networkName(account.accountDetails.Crypto.network)} · {shortAddress(account.accountDetails.Crypto.address)}
          </span>
        }
        {
          account.accountDetails.Fiat?.accountType.iban &&
          <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            EUR account · {account.accountDetails.Fiat.accountType.iban.iban}
          </span>
        }
        {
          account.accountDetails.Fiat?.accountType.us &&
          <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ACH · {account.accountDetails.Fiat.accountType.us.accountNumber}
          </span>
        }
      </div>
      {
        withdrawalKind === 'cash' && hasLikelyCurrencyMismatch(account, cashCurrency) &&
        <span style={{
          background: '#fdf3e2',
          border: '1px solid #f0d9ae',
          borderRadius: 20,
          color: '#9a6700',
          flexShrink: 0,
          fontSize: 11.5,
          fontWeight: 500,
          marginLeft: 'auto',
          padding: '2px 8px',
          whiteSpace: 'nowrap'
        }}>
          {getWithdrawalAccountCurrency(account)} account — check it accepts {cashCurrency}
        </span>
      }
    </div>
  }

  // A unified balance (asset set) is one pot across networks, so it names no
  // network here; the destination network is chosen further down.
  const renderAssetText = (asset: WithdrawableAsset, selected = false): React.ReactElement => {
    const method = getMethod(asset.network, asset.token)
    const showNetwork = !asset.asset
    const balance = `${formatAssetAmount(asset.maxAmount, method.ticker)} ${method.ticker}`

    if (!selected) {
      return <AssetRow
        method={method}
        size={20}
        network={showNetwork}
        subtitle={asset.treasuryId ? `Private treasury · ${assetSubtitle(method, { network: showNetwork })}` : undefined}
        trailing={balance}
        style={{ width: '100%' }} />
    }

    const balanceLabel = `Available ${balance}`
    return <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, width: '100%' }}>
      <AssetIcon method={method} size={20} badge={showNetwork} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          color: 'var(--ink, #20242d)',
          fontSize: 13.5,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {tickerLabel(method.ticker)}
          {showNetwork && <span style={{ color: 'var(--text-soft, #7d8492)' }}> on {methodNetworkLabel(method)}</span>}
        </span>
        <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {asset.treasuryId ? `Private treasury · ${balanceLabel}` : balanceLabel}
        </span>
      </div>
    </div>
  }

  const getNoSelectableAssetMessage = (): string => {
    if (withdrawalKind === 'crypto') {
      return 'No funded crypto assets are currently available.'
    }

    if (usesLegacyEurOfframp) {
      return 'No funded assets are currently available for bank withdrawals.'
    }

    if (cashCurrency === 'EUR') {
      return `EUR bank withdrawals currently require USDC on ${networkName(expectedUsdcNetwork)}.`
    }

    return 'No funded assets are currently available for this cash withdrawal currency.'
  }

  const renderCashCurrencyText = (
    option: typeof cashCurrencyConfig[number],
    selected = false
  ): React.ReactElement => (
    <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, opacity: option.enabled ? 1 : 0.45 }}>
      <img
        src={option.logo}
        style={{
          height: 20,
          width: 20,
          flexShrink: 0
        }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{
          color: 'var(--ink, #20242d)',
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.2
        }}>
          {option.currency}
        </span>
        {!selected && <span style={{
          color: 'var(--text-soft, #7d8492)',
          fontSize: 12.5,
          lineHeight: 1.25
        }}>
          {option.enabled ? option.label : `${option.label} · ${option.unavailableLabel ?? 'Coming soon'}`}
        </span>}
      </div>
    </div>
  )

  useEffect(() => {
    setIsLoadingAccounts(true)
    Radom
      .listWithdrawalAccounts()
      .then(a => setWithdrawalAccounts(a))
      .finally(() => setIsLoadingAccounts(false))
  }, [])

  useEffect(() => {
    setWithdrawalAccount(undefined)
    setWithdrawalAmount('')
    setFeeEstimate(null)
  }, [selectedAsset, selectedMethod, withdrawalKind, amountMode])

  useEffect(() => {
    if (!selectedAsset.asset) {
      return
    }
    const methods = methodsForTicker(selectedAsset.asset, testMode)
    if (methods.length === 0) {
      return
    }
    const matches = methods.some(m => methodNetworkName(m) === selectedAsset.network)
    if (matches) {
      return
    }
    const first = methods[0]
    setSelectedAsset(prev => ({
      ...prev,
      network: methodNetworkName(first),
      token: methodTokenAddress(first)
    }))
  }, [selectedAsset.asset, selectedAsset.network, testMode])

  useEffect(() => {
    if (!props.initialWithdrawalAccountId) {
      return
    }

    const preferredAccount = withdrawalAccounts.find(a => a.id === props.initialWithdrawalAccountId)
    if (preferredAccount && filterWithdrawalAccount(preferredAccount)) {
      setWithdrawalAccount(preferredAccount)
    }
  }, [props.initialWithdrawalAccountId, withdrawalAccounts, selectedAsset, withdrawalKind])

  useEffect(() => {
    if (!isIvyEurWithdrawal || !amountToWithdraw || Number(amountToWithdraw) <= 0) {
      setFeeEstimate(null)
      setIsEstimatingFee(false)
      return
    }

    let cancelled = false
    setIsEstimatingFee(true)
    const timeout = window.setTimeout(() => {
      Radom.getPayoutFee({
        currency: 'EUR',
        amount: amountToWithdraw,
        amountMode,
        accountType: 'business',
        fundingCurrency: selectedFundingCurrency
      })
        .then((estimate) => {
          if (!cancelled) {
            setFeeEstimate(estimate)
          }
        })
        .catch((err) => {
          console.error('Failed to estimate bank withdrawal fee', err)
          if (!cancelled) {
            setFeeEstimate(null)
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsEstimatingFee(false)
          }
        })
    }, 250)

    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [amountToWithdraw, amountMode, isIvyEurWithdrawal, selectedFundingCurrency])

  const onTokenWithdrawal = async (): Promise<void> => {
    if (!withdrawalAccount) {
      return
    }

    let isError = false

    setIsWithdrawing(true)
    const { network, token } = selectedAsset
    try {
      if (isSelectedIndividualIban && !usesLegacyEurOfframp) {
        throw new Error('EUR accounts that belong to individuals are paid from Send funds as a payout, not withdrawn to.')
      }

      if (isSelectedIban && !usesLegacyEurOfframp && !isSelectedBusinessIban) {
        throw new Error('This EUR account cannot be withdrawn to from here. Business-owned EUR accounts can; individual accounts are paid from Send funds.')
      }

      if (isIvyBankWithdrawal) {
        if (!isSelectedBusinessIban) {
          throw new Error('EUR bank withdrawals require a business-owned EUR account')
        }

        if (!isSupportedIvyFundingAsset) {
          throw new Error('EUR bank withdrawals are funded with USDC or USDT on Ethereum')
        }

        if (hasInsufficientBankFunding) {
          throw new Error(`Available ${selectedFundingCurrency} balance is lower than the estimated bank withdrawal debit`)
        }

        const user = await User.getUser()
        const createdPayout = await Radom.createPayout({
          idempotencyKey: cashSubmissionKey.current,
          payouts: [{
            gateway: 'fiatWithdrawalAccount',
            amount: amountToWithdraw,
            amountMode,
            withdrawalAccountId: withdrawalAccount.id,
            accountType: 'Business',
            fundingCurrency: selectedFundingCurrency,
            email: user.emailAddress || '',
            name: withdrawalAccount.accountDetails.Fiat?.accountOwnerName
          }],
          accountType: 'Business',
          isTestMode: testMode,
          deductFees: false,
          sendEmailReceipts: false
        })

        await Radom.confirmPayout(createdPayout.id)
        cashSubmissionKey.current = crypto.randomUUID()
        successToast('Successfully submitted bank withdrawal')
      } else {
        if (withdrawalKind === 'cash' && cashCurrency === 'USD' && !isSelectedUsAccount) {
          throw new Error('USD bank withdrawals require a saved US bank account.')
        }

        await Radom.submitWithdrawalRequest(
          network,
          amountToWithdraw,
          withdrawalAccount.id,
          senderPaysFees,
          token,
          selectedTreasuryId
        )
        successToast('Successfully submitted withdrawal request')
      }
      Treasury.loadBalances()
    } catch (err) {
      isError = true
      console.error(err)
      errorToast(err.response?.data?.error || err.message)
    }
    setIsWithdrawing(false)

    if (!isError) {
      props.onModalOpen(false)
    }
  }

  const formatAmount = (value?: number | string | null, decimals = 6): string => {
    if (value === undefined || value === null || value === '') return '-'
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) return String(value)
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    })
  }

  const hasSelectableAssets = selectableAssets.length > 0
  const isSelectedAssetSelectable = selectableAssets.some(asset => isSameAsset(asset, selectedAsset))
  const hasPositiveAmount = Number(amountToWithdraw) > 0
  const canSubmit =
    hasPositiveAmount &&
    isSelectedAssetSelectable &&
    !!withdrawalAccount &&
    !isEstimatingFee &&
    (!isIvyEurWithdrawal || (
      isSelectedBusinessIban &&
      isSupportedIvyFundingAsset &&
      !!feeEstimate &&
      !hasInsufficientBankFunding
    ))

  // Merchants say how much and where; the platform proposes the funding
  // balance. Auto-selection never picks a volatile asset and never overrides
  // an explicit choice made after opening the cash flow.
  const userPickedCashFunding = useRef(false)
  useEffect(() => {
    userPickedCashFunding.current = false
  }, [withdrawalKind, cashCurrency, props.modalOpen])
  useEffect(() => {
    if (withdrawalKind !== 'cash' || userPickedCashFunding.current) return
    if (cashFundingAssets.length === 0) return
    const stables = cashFundingAssets.filter(asset => {
      const method = getMethod(asset.network, asset.token)
      return method.ticker === 'USDC' || method.ticker === 'USDT'
    })
    const best = stables.sort((a, b) => Number(b.maxAmount) - Number(a.maxAmount))[0]
    if (best && !isSameAsset(best, selectedAsset)) {
      const selectedIsEligible = cashFundingAssets.some(asset => isSameAsset(asset, selectedAsset))
      if (!selectedIsEligible) {
        setSelectedAsset(best)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawalKind, cashCurrency, cashFundingAssets.length])

  const fundingAssetSelector = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <InputLabel style={{ opacity: 1 }}>{withdrawalKind === 'cash' ? 'Pay with' : 'Asset'}</InputLabel>
      {withdrawalKind === 'cash' &&
            <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12, lineHeight: 1.45, marginTop: -4 }}>
              Selected automatically from your largest balance — change it if you prefer another.
            </span>}
      {
        selectableAssets.length > 0
          ? <Dropdown
            onClose={() => setAssetSearch('')}
            outerStyle={{ minHeight: 40, backgroundColor: 'var(--surface, #ffffff)', border: '1px solid var(--border-strong, #d8dde7)' }}
            selectedContent={renderAssetText(selectedAsset, true)}
            dropdownContent={
              <DropdownContext.Consumer>
                {({ closeDropdown }) => <div style={{ minWidth: 320 }}>
                  <DropdownSearchContainer>
                    <DropdownTextInput
                      value={assetSearch}
                      onMouseDown={e => e.stopPropagation()}
                      onClick={e => e.stopPropagation()}
                      onChange={e => setAssetSearch(e.target.value)}
                      placeholder='Search assets'
                      style={{
                        border: '1px solid var(--border-strong, #d8dde7)',
                        borderRadius: 10,
                        boxSizing: 'border-box',
                        minHeight: 38,
                        padding: '0 10px'
                      }}
                      autoFocus />
                  </DropdownSearchContainer>
                  {searchedSelectableAssets.map(asset =>
                    <DropdownItem
                      key={`${asset.network}-${asset.token ?? 'native'}-${asset.treasuryId ?? 'radom'}`}
                      onClick={() => {
                        userPickedCashFunding.current = true
                        setSelectedAsset(asset)
                        closeDropdown()
                      }}
                      style={{ padding: '11px 12px' }}>
                      {renderAssetText(asset)}
                    </DropdownItem>
                  )}
                  {searchedSelectableAssets.length === 0 &&
                            <div style={{
                              color: 'var(--text-soft, #7d8492)',
                              fontSize: 12.5,
                              lineHeight: 1.45,
                              padding: '0 12px 14px'
                            }}>
                              No assets match this search.
                            </div>}
                </div>}
              </DropdownContext.Consumer>
            } />
          : <div style={{
            background: 'var(--surface, #ffffff)',
            border: '1px solid var(--border, #e8edf5)',
            borderRadius: 12,
            color: 'var(--text-soft, #7d8492)',
            fontSize: 12.5,
            lineHeight: 1.45,
            padding: '10px 12px'
          }}>
            {getNoSelectableAssetMessage()}
          </div>
      }
    </div>
  )

  const cardContent = (
    <SectionCard $embedded={props.embedded}>
      {!props.hideWithdrawalKindSelector && <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <InputLabel style={{ opacity: 1 }}>Withdrawal type</InputLabel>
        <div style={{
          background: 'var(--surface-hover, #f4f6fa)',
          border: '1px solid var(--border, #e7ebf2)',
          borderRadius: 12,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          padding: 3
        }}>
          {[
            { key: 'cash' as WithdrawalKind, label: 'Cash' },
            { key: 'crypto' as WithdrawalKind, label: 'Crypto' }
          ].map(option =>
            <SecondaryButton
              key={option.key}
              disabled={option.key === 'cash' && !FiatRamps.cashWithdrawalEnabled}
              onClick={() => {
                if (option.key === 'cash' && !FiatRamps.cashWithdrawalEnabled) return
                setWithdrawalKind(option.key)
                props.onWithdrawalKindChange?.(option.key)
              }}
              style={{
                background: withdrawalKind === option.key ? 'var(--surface, #ffffff)' : 'transparent',
                border: withdrawalKind === option.key ? '1px solid #e1e6ee' : '1px solid transparent',
                boxShadow: withdrawalKind === option.key ? '0 1px 2px rgba(20, 28, 45, 0.06)' : 'none',
                color: withdrawalKind === option.key ? 'var(--ink, #20242d)' : 'var(--text-muted, #697386)',
                minHeight: 34,
                padding: '0 10px'
              }}>
              {option.label}
            </SecondaryButton>
          )}
        </div>
      </div>}

      {withdrawalKind === 'cash' && <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        <InputLabel style={{ opacity: 1 }}>Cash currency</InputLabel>
        <Dropdown
          matchTriggerWidth
          onClose={() => setCashCurrencySearch('')}
          outerStyle={{ minHeight: 40, width: '100%' }}
          selectedContent={renderCashCurrencyText(
            cashCurrencyOptions.find(option => option.currency === cashCurrency) ?? cashCurrencyOptions[0],
            true
          )}
          dropdownContent={
            <DropdownContext.Consumer>
              {({ closeDropdown }) => <div>
                <DropdownSearchContainer>
                  <DropdownTextInput
                    value={cashCurrencySearch}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    onChange={e => setCashCurrencySearch(e.target.value)}
                    placeholder='Search currencies'
                    style={{
                      border: '1px solid var(--border-strong, #d8dde7)',
                      borderRadius: 10,
                      boxSizing: 'border-box',
                      minHeight: 38,
                      padding: '0 10px'
                    }}
                    autoFocus />
                </DropdownSearchContainer>
                {searchedCashCurrencyOptions.map(option => <DropdownItem
                  key={option.currency}
                  style={{
                    cursor: option.enabled ? 'pointer' : 'not-allowed',
                    color: 'var(--ink, #20242d)',
                    fontSize: 13.5,
                    fontWeight: 500,
                    lineHeight: 1.25,
                    pointerEvents: option.enabled ? undefined : 'none'
                  }}
                  onClick={() => {
                    if (!option.enabled) {
                      return
                    }

                    setCashCurrency(option.currency)
                    closeDropdown()
                  }}>
                  {renderCashCurrencyText(option)}
                </DropdownItem>)}
              </div>}
            </DropdownContext.Consumer>
          } />
      </div>}

      {withdrawalKind === 'crypto' && fundingAssetSelector}

      {withdrawalKind === 'cash' && fundingAssetSelector}

      {hasSelectableAssets && <>
        {withdrawalKind === 'crypto' && <AssetSummaryCard>
          <WithdrawalRow>
            <AssetIcon method={selectedMethod} size={34} badge={!selectedAsset.asset} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span style={{ color: 'var(--ink, #20242d)', fontSize: 15.25, fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {tickerLabel(selectedMethod.ticker)}
              </span>
              <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {assetSubtitle(selectedMethod, { network: !selectedAsset.asset })}
              </span>
            </div>
          </WithdrawalRow>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right' }}>
            <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: 11.5 }}>Available balance</span>
            <span style={{
              color: 'var(--ink, #20242d)',
              fontSize: 15.25,
              fontWeight: 500,
              maxWidth: 190,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {availableAmountLabel}
            </span>
          </div>
        </AssetSummaryCard>}

        {withdrawalKind === 'crypto' && destMethods.length > 1 &&
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <InputLabel style={{ opacity: 1 }}>Destination network</InputLabel>
            <Dropdown
              selectedContent={
                <span style={{ alignItems: 'center', display: 'flex', fontSize: 13.5, gap: 8, minWidth: 0 }}>
                  <NetworkIcon method={selectedMethod} size={20} />
                  {methodNetworkLabel(selectedMethod)}
                </span>
              }
              dropdownContent={
                destMethods.map((method, idx) => (
                  <DropdownItem
                    key={`${method.name}-${idx}`}
                    onClick={() => setSelectedAsset(prev => ({
                      ...prev,
                      network: methodNetworkName(method),
                      token: methodTokenAddress(method)
                    }))}>
                    <NetworkRow method={method} size={20} style={{ width: '100%' }} />
                  </DropdownItem>
                ))
              }
            />
          </div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <InputLabel style={{ opacity: 1 }}>
            {withdrawalKind === 'cash'
              ? isIvyEurWithdrawal
                ? amountMode === 'recipient' ? 'Recipient receives' : 'You pay'
                : 'Funding amount'
              : 'Withdrawal amount'}
          </InputLabel>
          {
            withdrawalKind === 'cash' && isIvyEurWithdrawal &&
                <div style={{
                  background: 'var(--surface-hover, #f4f6fa)',
                  border: '1px solid var(--border, #e7ebf2)',
                  borderRadius: 12,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  padding: 3
                }}>
                  {[
                    { key: 'recipient' as PayoutAmountMode, label: 'Recipient gets' },
                    { key: 'source' as PayoutAmountMode, label: 'You pay' }
                  ].map(option =>
                    <SecondaryButton
                      key={option.key}
                      onClick={() => setAmountMode(option.key)}
                      style={{
                        background: amountMode === option.key ? 'var(--surface, #ffffff)' : 'transparent',
                        border: amountMode === option.key ? '1px solid #e1e6ee' : '1px solid transparent',
                        boxShadow: amountMode === option.key ? '0 1px 2px rgba(20, 28, 45, 0.06)' : 'none',
                        color: amountMode === option.key ? 'var(--ink, #20242d)' : 'var(--text-muted, #697386)',
                        minHeight: 32,
                        padding: '0 10px'
                      }}>
                      {option.label}
                    </SecondaryButton>
                  )}
                </div>
          }
          <div style={{ display: 'flex', flexGrow: 1, gap: 10 }}>
            <NumberInput
              disabled={isWithdrawing}
              value={amountToWithdraw}
              onUserInput={e => setWithdrawalAmount(e)}
              fontSize='14.5'
              align='left'
              style={{ minHeight: 40 }}
              placeholder='Enter amount'
              required={true}
            />
            {
              withdrawalKind === 'cash'
                ? <>
                  <div style={{
                    alignItems: 'center',
                    border: '1px solid #e1e6ee',
                    borderRadius: 10,
                    color: 'var(--ink, #20242d)',
                    display: 'flex',
                    fontSize: 13,
                    fontWeight: 500,
                    minHeight: 40,
                    padding: '0 12px'
                  }}>{isIvyEurWithdrawal ? amountMode === 'recipient' ? 'EUR' : selectedFundingCurrency : selectedMethod.ticker}</div>
                  {
                    (!isIvyEurWithdrawal || amountMode === 'source') &&
                        <SecondaryButton
                          style={{ minHeight: 40, minWidth: 66, padding: '0 12px', flexShrink: 0 }}
                          disabled={isWithdrawing}
                          onClick={() => setWithdrawalAmount(availableAmount)}>
                          Max
                        </SecondaryButton>
                  }
                </>
                : <SecondaryButton
                  style={{ minHeight: 40, minWidth: 66, padding: '0 12px', flexShrink: 0 }}
                  disabled={isWithdrawing}
                  onClick={() => setWithdrawalAmount(availableAmount)}>
                      Max
                </SecondaryButton>
            }
          </div>
        </div>

        {
          isIvyEurWithdrawal &&
            <div style={{
              background: 'var(--surface, #ffffff)',
              border: `1px solid ${hasInsufficientBankFunding ? '#f1c7c7' : 'var(--border, #e8edf5)'}`,
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 7,
              padding: '10px 12px'
            }}>
              {
                isEstimatingFee
                  ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 28 }} />
                  : <>
                    {
                      amountMode === 'source' &&
                      <EstimateRow>
                        <EstimateLabel>Recipient receives</EstimateLabel>
                        <EstimateValue>{formatAmount(feeEstimate?.estimatedRecipientAmount, 2)} EUR</EstimateValue>
                      </EstimateRow>
                    }
                    <EstimateRow>
                      <EstimateLabel>You pay</EstimateLabel>
                      <EstimateValue>{formatAmount(feeEstimate?.estimatedTotalDebitUsd)} {selectedFundingCurrency}</EstimateValue>
                    </EstimateRow>
                    <EstimateRow>
                      <EstimateLabel>Funding amount</EstimateLabel>
                      <EstimateValue>{formatAmount(feeEstimate?.estimatedFundingAmountUsd)} {selectedFundingCurrency}</EstimateValue>
                    </EstimateRow>
                    <EstimateRow>
                      <EstimateLabel>Fee</EstimateLabel>
                      <EstimateValue>
                        {formatAmount(feeEstimate?.estimatedFeeUsd)} {selectedFundingCurrency}
                      </EstimateValue>
                    </EstimateRow>
                    <EstimateRow>
                      <EstimateLabel>Rate</EstimateLabel>
                      <EstimateValue>1 {selectedFundingCurrency} ≈ {formatAmount(feeEstimate?.exchangeRate, 8)} EUR</EstimateValue>
                    </EstimateRow>
                    <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12 }}>All fees are included in the rate.</span>
                  </>
              }
              {
                hasInsufficientBankFunding &&
                <span style={{ color: '#b42318', fontSize: 12.25, lineHeight: 1.4 }}>
                  Available {selectedFundingCurrency} balance is lower than the estimated debit.
                </span>
              }
            </div>
        }

        {selectedTreasuryId && <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: '10px 12px',
          borderRadius: 12,
          border: '1px solid var(--border, #e8edf5)',
          background: 'var(--surface, #ffffff)'
        }}>
          <span style={{ color: 'var(--ink, #20242d)', fontSize: 13, fontWeight: 500 }}>Private treasury</span>
          <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, lineHeight: 1.45 }}>
                This withdrawal will be sent directly from the organization&apos;s private treasury.
          </span>
        </div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <InputLabel style={{ opacity: 1 }}>Withdrawal destination</InputLabel>
          {
            isLoadingAccounts
              ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 40 }} />
              : filteredAccounts.length > 0
                ? <Dropdown
                  disabled={isLoadingAccounts}
                  onClose={() => setAccountSearch('')}
                  outerStyle={{ minHeight: 40 }}
                  selectedContent={
                    withdrawalAccount
                      ? renderAccountText(withdrawalAccount)
                      : <span style={{ fontSize: 13.5, color: 'var(--icon-muted, #747894)' }}>Select account</span>
                  }
                  dropdownContent={
                    <DropdownContext.Consumer>
                      {({ closeDropdown }) => <div style={{ minWidth: 320 }}>
                        <DropdownSearchContainer>
                          <DropdownTextInput
                            value={accountSearch}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setAccountSearch(e.target.value)}
                            placeholder='Search accounts'
                            style={{
                              border: '1px solid var(--border-strong, #d8dde7)',
                              borderRadius: 10,
                              boxSizing: 'border-box',
                              minHeight: 38,
                              padding: '0 10px'
                            }}
                            autoFocus />
                        </DropdownSearchContainer>
                        {searchedAccounts
                          .filter(t => t.id !== withdrawalAccount?.id)
                          .map(t =>
                            <DropdownItem
                              key={t.id}
                              onClick={() => {
                                setWithdrawalAccount(t)
                                closeDropdown()
                              }}
                              style={{ padding: '11px 12px' }}>
                              {renderAccountText(t)}
                            </DropdownItem>
                          )}
                        {searchedAccounts.filter(t => t.id !== withdrawalAccount?.id).length === 0 &&
                            <div style={{
                              color: 'var(--text-soft, #7d8492)',
                              fontSize: 12.5,
                              lineHeight: 1.45,
                              padding: '8px 12px 14px'
                            }}>
                              No accounts match this search.
                            </div>}
                      </div>}
                    </DropdownContext.Consumer>
                  } />
                : showIndividualIbanPayoutHint
                  ? <div style={{
                    background: 'var(--surface, #ffffff)',
                    border: '1px solid var(--border, #e8edf5)',
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    padding: '10px 12px'
                  }}>
                    <span style={{ color: 'var(--ink, #20242d)', fontSize: 13, fontWeight: 500 }}>
                        Individual IBAN recipients are paid from Payouts
                    </span>
                    <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, lineHeight: 1.45 }}>
                          Use this withdrawal modal for business-owned EUR IBANs. To pay an individual&apos;s IBAN,
                          create a payout and choose their IBAN payout account.
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <SecondaryButton onClick={() => {
                        navigate('/payouts/create')
                        props.onModalOpen(false)
                      }} style={{ minHeight: 36 }}>Create payout</SecondaryButton>
                      <SecondaryButton onClick={() => {
                        if (props.onCreateWithdrawalAccount) {
                          props.onCreateWithdrawalAccount('cash')
                        } else {
                          navigate('/accounts')
                          props.onModalOpen(false)
                        }
                      }} style={{ minHeight: 36 }}>Add business EUR account</SecondaryButton>
                    </div>
                  </div>
                  : <Gate permission='WriteWithdrawalAccount' fallback={
                    <span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12, lineHeight: 1.45 }}>
                      No withdrawal accounts are currently available.
                      You need permission to create a withdrawal account.
                    </span>
                  }>
                    <SecondaryButton onClick={() => {
                      if (props.onCreateWithdrawalAccount) {
                        props.onCreateWithdrawalAccount(withdrawalKind)
                      } else {
                        navigate('/accounts')
                        props.onModalOpen(false)
                      }
                    }} style={{ width: '100%', minHeight: 40 }}>Create withdrawal account</SecondaryButton>
                  </Gate>

          }
        </div>
      </>}

    </SectionCard>
  )

  const footerContent = hasSelectableAssets && filteredAccounts.length > 0 && (
    <ModalFooter $fullWidthAction>
      <PrimaryButton
        disabled={!canSubmit}
        isLoading={isWithdrawing}
        style={{ width: '100%' }}
        onClick={onTokenWithdrawal}>
        {withdrawalKind === 'cash' ? 'Withdraw to bank' : 'Withdraw'}
      </PrimaryButton>
    </ModalFooter>
  )

  if (props.embedded) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cardContent}
          {footerContent}
        </div>
      </div>
    )
  }

  return (
    <Modal
      visible={props.modalOpen}
      innerStyles={{ width: 476 }}
      onClose={() => props.onModalOpen(false)}
      title='Withdraw'>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {cardContent}
      </div>
      {footerContent}
    </Modal>
  )
})

export default AssetWithdrawalModal
